// 將「會員等級 / VIP 等級」清單統一轉成字串陣列
// 可接受陣列、逗號分隔字串、null/undefined/空字串，最終都轉成過濾掉空值的字串陣列
function normalizePaymentList(value) {
  if (Array.isArray(value)) return value.map(item => String(item)).filter(Boolean)
  if (value === null || value === undefined || value === '') return []
  return String(value).split(',').map(item => item.trim()).filter(Boolean)
}

function normalizePaymentFee(paymentFee, type) {
  if (!paymentFee || typeof paymentFee !== 'object') return {}
  return paymentFee[type] || paymentFee
}

// 組出存款金額上下限物件，min/max 若未提供則預設為 '0'
function buildAmountLimit(min, max) {
  return {
    min: min === undefined || min === null ? '0' : min,
    max: max === undefined || max === null ? '0' : max
  }
}

// 依 id 從清單中找出對應的項目，找不到則回傳空物件，避免後續取屬性時噴錯
function findPaymentItem(list, id) {
  return (list || []).find(item => String(item.id) === String(id)) || {}
}

// 同組渠道隨機顯示：同一 group_code 只保留一筆，且 session 內固定
// （同一分頁 session 顯示同一筆、重整不變；抽中那筆之後被後端濾掉才重抽）
const GROUP_PICK_KEY = 'depositGroupPick'
function loadGroupPicks() { try { return JSON.parse(sessionStorage.getItem(GROUP_PICK_KEY)) || {} } catch (e) { return {} } }
function saveGroupPicks(picks) { try { sessionStorage.setItem(GROUP_PICK_KEY, JSON.stringify(picks)) } catch (e) { console.log(e) } }
function pickRandomPerGroup(methods) {
  const groups = {}
  const result = []
  methods.forEach(m => {
    if (!m.group_code) { result.push(m); return }
    (groups[m.group_code] = groups[m.group_code] || []).push(m)
  })
  const picks = loadGroupPicks()
  Object.keys(groups).forEach(g => {
    const list = groups[g]
    let chosen = list.find(m => String(m.id) === String(picks[g]))
    if (!chosen) { chosen = list[Math.floor(Math.random() * list.length)]; picks[g] = chosen.id }
    result.push(chosen)
  })
  saveGroupPicks(picks)
  return result
}

// 判斷會員國籍是否符合限用國籍清單
// 未設定限用國籍（清單為空）則不檢查；有設定時，會員 nationality 為 null／空字串或不在清單內即拒絕
export function isNationalityAllowed(allowNationality, memberInfo) {
  const allowList = normalizePaymentList(allowNationality)
  if (allowList.length === 0) return true
  const nationality = memberInfo && memberInfo.nationality
  if (!nationality) return false
  return allowList.includes(String(nationality))
}

// 判斷該存款方式是否允許此會員使用
// 條件：會員等級在允許清單內，且（沒有限制 VIP 等級 或 VIP 等級在允許清單內），且國籍符合限用國籍
function isDepositPaymentAllowed(payment, memberInfo) {
  const allowMemberLevel = payment.allow_member || []
  const allowVipLevel = payment.allow_vip || []
  const isMemberAllowed = allowMemberLevel.includes(String(memberInfo.vl))
  const isVipAllowed = allowVipLevel.includes(String(memberInfo.vip_level_id))
  const isNationAllowed = isNationalityAllowed(payment.allow_nationality, memberInfo)
  return isMemberAllowed && isVipAllowed && isNationAllowed
}

// 將後端回傳的存款資料（payment_provider / brand_payment_provider / payment_channel 等）
// 整理、合併成前端可直接使用的存款方式清單
export function buildDepositPaymentMethods(paymentData) {
  const providers = paymentData.payment_provider || [] // 所有金流商
  const providerSettings = paymentData.brand_payment_provider || [] // 品牌底下對金流商的設定
  const methods = []

  // 逐一走訪每個「存款管道」(channel)
  ;(paymentData.payment_channel || []).forEach(channel => {
    // 再逐一走訪每個品牌金流商設定
    providerSettings.forEach(providerSetting => {
      // 找出此設定對應的金流商，以及該金流商底下的所有管道設定
      const provider = findPaymentItem(providers, providerSetting.payment_provider_id)
      const providerChannels = provider.payment_provider_channel || []
      // 篩選出屬於目前 channel 的品牌管道設定，逐一處理
      ;(providerSetting.brand_payment_provider_channel || [])
        .filter(channelSetting => Number(channelSetting.status) === 1) // 只取啟用中（status 為 1）的品牌管道設定
        .filter(channelSetting => String(channelSetting.payment_channel_id) === String(channel.id))
        .forEach(channelSetting => {
          // 找出此品牌管道設定對應的金流商管道設定
          const providerChannel = providerChannels.find(item =>
            String(item.id) === String(channelSetting.payment_provider_channel_id)
          ) || {}

          // 計算實際存款金額上下限：優先使用品牌管道設定 -> 金流商管道設定 -> 金流商預設
          const amountLimit = buildAmountLimit(
            channelSetting.deposit_min || providerChannel.min_amount || provider.min_deposit,
            channelSetting.deposit_max || providerChannel.max_amount || provider.max_deposit
          )
          // 計算「前台顯示用」的金額上下限：優先使用品牌管道設定的前台欄位，否則沿用上面算出的實際限額
          const amountLimitFront = buildAmountLimit(
            channelSetting.deposit_min_front || amountLimit.min,
            channelSetting.deposit_max_front || amountLimit.max
          )

          // 將金流商、品牌設定、管道設定的資料合併成一筆完整的存款方式
          methods.push({
            ...provider,
            ...channelSetting,
            payment_id: channelSetting.id,
            id: channelSetting.id,
            brand_payment_provider_channel_id: channelSetting.id,
            // 同組渠道代碼（來源：payment_provider_channel.other.group_code，後台自定）；同組只會顯示一筆
            group_code: (providerChannel.other && typeof providerChannel.other === 'object' && providerChannel.other.group_code) ? String(providerChannel.other.group_code) : '',
            name: providerSetting.name || provider.name, // 顯示名稱：優先用品牌自訂名稱
            code: provider.code || provider.class_name, // 金流商代碼
            image: providerSetting.image, // 顯示圖示：來源為 brand_payment_provider.image
            announcement: providerSetting.announcement || '', // 公告文字
            label: providerSetting.label || [], // 標籤
            amount_limit: amountLimit, // 實際存款金額限制
            amount_limit_front: amountLimitFront, // 前台顯示用的存款金額限制
            allow_member: normalizePaymentList(channelSetting.allow_member), // 允許使用的會員等級清單（來源：brand_payment_provider_channel.allow_member）
            allow_vip: normalizePaymentList(channelSetting.allow_vip), // 允許使用的 VIP 等級清單（來源：brand_payment_provider_channel.allow_vip）
            allow_nationality: normalizePaymentList(channelSetting.allow_nationality), // 允許使用的國籍清單（來源：brand_payment_provider_channel.allow_nationality，null／空字串代表不限制）
            payment_fee: normalizePaymentFee(providerSetting.payment_fee, 'deposit'), // 手續費設定
            payment_fee_module: channelSetting.payment_fee_module || providerSetting.payment_fee_module, // 手續費計算模組
            exchange_rate: providerChannel.exchange_rate || {}, // 匯率設定
            deposit_rate: channelSetting.deposit_rate || providerChannel.deposit_rate || 0, // 存款比例/匯率
            show_custom_amount: channelSetting.show_custom_amount, // 是否顯示自訂金額輸入
            price_list: channelSetting.prices || [], // 快速選擇金額清單
            prices_currency: channelSetting.prices_currency || null, // 該金流預設幣別
            // 以下為存款管道本身的顯示資訊
            typeName: channel.name,
            typeCode: channel.code,
            typeNum: channel.type,
            typeSort: providerSetting.sort,
            typeImage: channel.image
          })
        })
    })
  })

  return pickRandomPerGroup(methods).sort((a, b) => (a.typeSort || 0) - (b.typeSort || 0))
}

// 將存款方式整理成「平鋪清單」，只保留 type 1,2,3,4,5，不再依管道類型分組
// 同一管道類型下同名的存款方式會去重，最終依 API 回傳的 brand_payment_provider.sort 排序
export function buildDepositPaymentList(payments, memberInfo) {
  const seen = new Set()
  return payments
    .filter(payment => [1, 2, 3, 4, 5].includes(payment.typeNum))
    .filter(payment => {
      const key = `${payment.typeNum}|${payment.name}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .filter(payment => isDepositPaymentAllowed(payment, memberInfo))
    .sort((a, b) => (a.typeSort || 0) - (b.typeSort || 0))
}
